import {Button, Icon, Loading, Tooltip} from '@startlibs/components'
import {Errors, WithForm} from '@startlibs/form'
import { useNavigate } from '@startlibs/components';
import {useToggle} from '@startlibs/core'
import {will} from '@startlibs/utils'
import React, {useEffect, useLayoutEffect, useRef, useState} from 'react'
import _ from 'lodash/fp'
import styled from 'styled-components'
import {Answer, ExpertAdditionalInfo} from '../request/forms/Answer'
import {CASE_CLOSED, CASE_REVIEWED} from '../enums/CaseState'
import {Card, PageContainer, PageFooter, SectionHeading} from '../components/PageLayout'
import {Header} from '../components/Header'
import {InfoBox, SuccessBox} from '../components/InfoBox'
import {PurviewFooter} from '../components/PurviewFooter'
import {QuestionList} from '../components/QuestionList'
import {getJwt} from '../hooks/useJwt'
import {hasAnsweredAll} from '../request/utils'
import {jwtPostFetcher} from '../utils/authFetch'
import {useConfirmExit} from '../admin/steps/hooks/useConfirmExit'
import {getColor} from '@startlibs/utils'
import {ExpertSignatureManagement} from './ExpertSignatureManagement'
import { FeedbackDialog } from './dialogs/FeedbackDialog'
import { IfLocale } from '../hocs/IfLocale'
import { setNotification } from '../components/Notifications'
import { isRichText, safeMultipleNewLineToBr, safeMultipleNewLineToBrOnly } from '../utils/utils'
import {FormattedDate, FormattedMessage, FormattedTime, useIntl} from "react-intl";
import { buildValidation } from '../utils/validation';
import { lazyProviderInfoSec } from '../components/WithProvider';
import { ExpertTimeFormCard, ExpertTimeHeading, getExpertTimeErrorMessage } from '../request/expertTime/ExpertTime';
import { EXPERT_TIME_HIDDEN, EXPERT_TIME_REQUIRED, getExpertTimeMode, useOwnExpertTime, validateExpertTime } from '../request/expertTime/expertTimeUtils';


export const Dateinfo = ({timeSavedDate, timeNow}) => {
  const intl = useIntl()
  // Calculate diff
  // ------------------------------------------------------------------------------------
  
  const diffTime = Math.abs(timeNow - timeSavedDate);
  const diffSeconds = Math.ceil(diffTime / (1000));
  const diffMinutes = Math.ceil(diffTime / (1000 * 60 ));
  const diffHours = Math.ceil(diffTime / (1000 * 60 * 60 ));
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 

  // < 1min: few seconds ago
  // > 1min < 1h: 15 minutes ago
  // > 1h < 1 day: 8 hours ago
  // > 1 day < 1 month: 27 days ago
    const periodType = diffSeconds < 60
    ? 'seconds'
    : diffMinutes < 60
      ? 'minutes'
      : diffHours < 24
        ? 'hours'
        : 'days'
  const period = diffSeconds < 60
    ? diffSeconds
    : diffMinutes < 60
      ? diffMinutes
      : diffHours < 24
        ? diffHours
        : diffDays

  var printableInfo = intl.formatMessage({
    defaultMessage: "Autosaved {period} {periodType, select, seconds {seconds ago} minutes {minutes ago} hours {hours ago} days {days ago} other {}}",

    description: "Expert review autosave label"
  }, {
      periodType,
      period
  })
  
  // ------------------------------------------------------------------------------------
  
  return  <Tooltip whenEllipsis={false} content={
              <div css="text-align: center;"><FormattedMessage
                defaultMessage="Autosaved on {time}"
                description="Expert review autosave tooltip"
                values={{time: <><FormattedDate value={timeSavedDate}/>, <FormattedTime value={timeSavedDate} /></>}}
              /></div> 
          }>
              <span>{printableInfo}</span>
          </Tooltip>

}

const RetryButton = styled(Button)`
  color: ${getColor('alert')};
  font-weight: 600;
  margin-left: 1rem;
  margin-top: -2px;
  margin-bottom: -2px;
`

const QuestionsWrapper = styled(Card)`
  position: relative;
`

const QUESTIONS_PATH = 'caseInfo.questionsConsultant'
const SIGNATURE_PATH = 'caseInfo.signatures'
const ADDITIONAL_INFO_PATH = 'caseInfo.additionalFields'
const EXPERT_TIME_PATH = 'expertTimeMinutes'
const GET_EMPTY_ADDITIONAL_INFO = () => ({title: '', content: '', id: Date.now().toString(36) + '', createdByExpert: true})

const transform = _.flow(
  _.pick(['questionsConsultant','additionalFields','signatures']),
  _.update('additionalFields',_.filter(({createdByExpert,title,content}) => !createdByExpert || title || content))
)

export const ExpertAnswers = ({caseRequest, setCaseRequest,feedbackPopup}) => {

  const providerInfoSec = lazyProviderInfoSec.read()
  const isSignatureRequired = providerInfoSec?.reportSignatureRequired || false
  const navigate = useNavigate()
  const intl = useIntl()
  const readOnly = (caseRequest.state === CASE_REVIEWED || caseRequest.state === CASE_CLOSED) || caseRequest.readOnly

  const [dtAutoSaved, setDtAutoSaved] = useState()
  const [timeNow, setTimeNow] = useState()

  const AUTO_SAVE_INTERVAL = 60000;

  useEffect(() => {
    const interval = setInterval(() => {
      setCaseRequest(updateQuestionsOnCase())
      autoSave()
    }, AUTO_SAVE_INTERVAL);
  
    return () => clearInterval(interval); 
  }, [])

  const UPDATE_TIME_INTERVAL = 10000;
  useEffect(() => {
    const intervalCalc = setInterval(() => {
      var now = new Date()
      setTimeNow(now)
    }, UPDATE_TIME_INTERVAL);
  
    return () => clearInterval(intervalCalc); 
  }, [])

  const formRef = useRef()
  const signaturesRef = useRef()
  const expertTimeRef = useRef()
  const expertTimeMode = getExpertTimeMode(providerInfoSec)
  const showExpertTime = expertTimeMode !== EXPERT_TIME_HIDDEN
  const isExpertTimeRequired = expertTimeMode === EXPERT_TIME_REQUIRED
  const expertTime = useOwnExpertTime(caseRequest.requestId)

  useEffect(() => {
    if (expertTime.isLoaded && formRef.current) {
      formRef.current.setValue(EXPERT_TIME_PATH, expertTime.entry?.minutes ?? null, false)
    }
    if (expertTime.isLoaded && window.location.hash === '#time') {
      setTimeout(() => {
        expertTimeRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'})
        expertTimeRef.current?.querySelector('input')?.focus({preventScroll: true})
      }, 100)
    }
  }, [expertTime.isLoaded])

  useEffect(() => {
    if (window.location.hash === '#signature' && isSignatureRequired) {
      setTimeout(() => {
        if (formRef.current) {
          formRef.current.addErrors({'caseInfo.signatures': intl.formatMessage({
            defaultMessage: 'You must sign the review',
            description: 'Expert review signature required validation error'
          })})
        }
        setTimeout(() => {
          signaturesRef.current?.scrollIntoView({behavior: 'smooth'})
        }, 50)
      }, 100)
    }
  }, [])

  const updateQuestionsOnCase = () => _.flow(
    _.set(QUESTIONS_PATH, formRef.current.getValue(QUESTIONS_PATH)),
    _.set(SIGNATURE_PATH, formRef.current.getValue(SIGNATURE_PATH)),
    _.set(ADDITIONAL_INFO_PATH, formRef.current.getValue(ADDITIONAL_INFO_PATH))
  )
  const savingLoading = useToggle()
  const saveButtonLoading = useToggle()
  const savingPreview = useToggle()
  const hasEmptyAnswers = useToggle(!hasAnsweredAll(caseRequest.caseInfo.questionsConsultant) && !caseRequest.report?.customReport)
  const caseCompletedWithoutReport = caseRequest.state === CASE_CLOSED && !caseRequest.report?.customReport && !caseRequest.report?.useCustomReport && !caseRequest.report
  // const saveFetch = () => jwtPostFetcher(getJwt())('/api/expert/questionsAnswers?requestId=' + caseRequest.requestId, transform(formRef.current.getValue('caseInfo')), {method: 'PUT'})
  const reviewPayload = () => {
    const payload = transform(formRef.current.getValue('caseInfo'))
    const minutes = formRef.current.getValue(EXPERT_TIME_PATH)
    return !showExpertTime || validateExpertTime(minutes, true) ? payload : {...payload, expertTimeMinutes: minutes}
  }
  const saveFetch = () => jwtPostFetcher(getJwt())('/api/reviewer/questionsAnswers?requestId=' + caseRequest.requestId, reviewPayload(), {method: 'PUT'})

  const save = () => saveButtonLoading.wait(saveFetch().then(will(navigate, "/expert")))

  const autoSave = () => {
    
    const trySave = () => {

      if(!savingLoading.isOpen){
        savingLoading.wait(
          saveFetch()
            .then(
              () => {
                var now = new Date()
                setDtAutoSaved(now)
                setTimeNow(now)
              }
            )
            .catch(
              () => setNotification({type: "alert", msg: (close) => <><FormattedMessage
                  defaultMessage="Changes could not be saved."
                  description="Expert review autosave error message"
                /><RetryButton small white onClick={_.flow(close,trySave)}><FormattedMessage
                  defaultMessage="Retry"
                  description="Expert review autosave error retry button"
                /></RetryButton></>})
            )
        )
      }

    }
    
    if (formRef && formRef.current && formRef.current.hasChanged && !readOnly) {
      trySave()
    }
  }
  
  useLayoutEffect(() => {
    return () => {
      setCaseRequest(updateQuestionsOnCase())
      autoSave()
    }
  }, [])
  
  useConfirmExit(formRef)

  const parseAdditionalFieldContent = _.update('content',  (content) => isRichText(content) ? safeMultipleNewLineToBrOnly(content) : safeMultipleNewLineToBr(content))
  const parseAnswer = _.update('answer',  (answer) => isRichText(answer) ? safeMultipleNewLineToBrOnly(answer) : safeMultipleNewLineToBr(answer))
  const parse = _.flow(
    _.update('caseInfo.additionalFields',_.map(item => item.content.includes('\n') ? parseAdditionalFieldContent(item) : item)),
    _.update('caseInfo.questionsConsultant',_.map(item => item.answer.includes('\n') ? parseAnswer(item) : item))
    
  )

  const setAndPreview = () =>
    savingPreview.wait(saveFetch()
      .then(
        _.flow(
          () => setCaseRequest(updateQuestionsOnCase()),
          will(navigate, `/expert/case/${caseRequest.requestId}/${hasEmptyAnswers.get() ? 'upload-report': 'report'}`)
        )
      ))

  const navigationButtons = (form) => readOnly
    ? <>
      <Button onClick={() => navigate(`/expert/case/${caseRequest.requestId}/overview`)}><FormattedMessage
        defaultMessage="Go to overview"
        description="Expert review go to overview button"
      /></Button>
      <Button highlight onClick={() => navigate(`/expert/case/${caseRequest.requestId}/report`)}><FormattedMessage
        defaultMessage="View report"
        description="Expert review, view report button"
      /></Button>
    </>
    : <>
      {dtAutoSaved && <SuccessBox css="margin-right:1rem;">
        <Icon icon="check"/>
        <Dateinfo timeSavedDate={dtAutoSaved} timeNow={timeNow}/>
      </SuccessBox>}
      <Button isLoading={saveButtonLoading.isOpen} onClick={save}><FormattedMessage
        defaultMessage="Save and view all cases"
        description="Expert review, save and view all cases button"
      /></Button>
      {
        hasEmptyAnswers.isOpen
          ? <Button highlight isLoading={form.isLoading} type="submit"><FormattedMessage
            defaultMessage="Skip and upload PDF report"
            description="Expert review, skip and upload PDF report button"
          /></Button>
          : <Button highlight isLoading={form.isLoading} type="submit"><FormattedMessage
            defaultMessage="Save and advance"
            description="Expert review, save and advance button"
          /></Button>
      }

      {/*<Button isLoading={savingPreview.isOpen} onClick={setAndPreview}>Preview</Button>
      */}
    </>


  return <WithForm
    alwaysSave
    // values={caseRequest}
    values={parse(caseRequest)}
    action={setAndPreview}
    ref={formRef}
    onChange={(_,caseRequest) => hasEmptyAnswers.openWith(!hasAnsweredAll(caseRequest.caseInfo.questionsConsultant) && !caseRequest.report?.customReport)}
    preValidation={buildValidation({
      'caseInfo.signatures': (signatures = []) => {
        if (isSignatureRequired && !signatures?.length && !hasEmptyAnswers.isOpen) {
          signaturesRef.current?.scrollIntoView({behavior: 'smooth'})
          return intl.formatMessage({
            defaultMessage: "You must sign the review",
            description: "Expert review signature required validation error"
          })
        }
      },
      [EXPERT_TIME_PATH]: (minutes) => {
        if (readOnly || !showExpertTime) return
        const message = getExpertTimeErrorMessage(intl, minutes ?? null, isExpertTimeRequired)
        if (message) {
          expertTimeRef.current?.scrollIntoView({behavior: 'smooth'})
          return message
        }
      }
    })}
  >{form => <>
    <PageContainer>
      {
         feedbackPopup.isOpen && <FeedbackDialog feedbackPopup={feedbackPopup} requestId={caseRequest.requestId} />
      }
      <Header isExpert title={intl.formatMessage({
        defaultMessage:"Expert review",
        description:"Expert review title"
      })}>
        {navigationButtons(form)}
      </Header>
      {
        caseRequest.report?.customReport && !readOnly &&
        <InfoBox lightBlue>
          {
          caseRequest.report?.useCustomReport
            ? <FormattedMessage
              defaultMessage="A custom PDF was uploaded and is serving as the case report."
              description="Expert review, custom PDF uploaded message"
            />
            : <FormattedMessage
              defaultMessage="Using the default report, but a custom PDF is also available."
              description="Expert review, custom PDF available message"
            />
          }
        </InfoBox>
      }
      <SectionHeading>
        <h3><FormattedMessage
          defaultMessage="Questions & answers"
          description="Expert review questions and answers section title"
        /></h3>
      </SectionHeading>
      <QuestionsWrapper>
        {readOnly && caseCompletedWithoutReport && 
          <SuccessBox topRight>
            <Icon icon="check"/> <span><FormattedMessage
            defaultMessage="The case has been marked as completed, but no report was generated."
            description="Expert review, case completed without report message"
          /></span>
          </SuccessBox>
        }
        {readOnly && !caseCompletedWithoutReport &&
          <SuccessBox topRight>
            <Icon icon="check"/> <span><FormattedMessage
            defaultMessage="This review has already been submitted."
            description="Expert review, already submitted message"
          /></span>
          </SuccessBox>
        }
        <QuestionList
          limit={1}
          path="caseInfo.questionsConsultant"
        >{(question, i) =>
          <Answer
            submitted={readOnly}
            i={i} key={question.id}
            form={form}
            path={['caseInfo', 'questionsConsultant']}
            onBlur={autoSave}
            requestId={caseRequest.requestId}
          />
        }</QuestionList>
      </QuestionsWrapper>
      <IfLocale not contains="LEGAL">
        <SectionHeading>
          <h3><FormattedMessage
            defaultMessage="Additional information"
            description="Expert review additional information section title"
          /></h3>
        </SectionHeading>
        <QuestionsWrapper>
          <QuestionList
            disabled={readOnly}
            path="caseInfo.additionalFields"
            addLabel={intl.formatMessage({
              defaultMessage: "Add a new field",
              description: "Expert review add new field button"
            })}
            getEmpty={GET_EMPTY_ADDITIONAL_INFO}
          >{(question, i) =>
            question.createdByExpert
            ? <ExpertAdditionalInfo
                onBlur={autoSave}
                submitted={readOnly}
                i={i} key={question.id}
                form={form}
                path={['caseInfo', 'additionalFields']}
                requestId={caseRequest.requestId}
              />
            : <Answer
                submitted={readOnly}
                i={i}
                key={question.id}
                form={form}
                path={['caseInfo', 'additionalFields']}
                questionNumber={null}
                onBlur={autoSave}
                questionKey='title'
                answerKey='content'
                requestId={caseRequest.requestId}
              />
          }</QuestionList>
        </QuestionsWrapper>
      </IfLocale>
      {showExpertTime && expertTime.isLoaded && (!readOnly || expertTime.entry) &&
        <div ref={expertTimeRef}>
          <ExpertTimeHeading
            required={isExpertTimeRequired && !readOnly}
            hasError={formRef?.current?.hasErrors(EXPERT_TIME_PATH)}
            showNotice={readOnly}
          />
          <ExpertTimeFormCard
            path={EXPERT_TIME_PATH}
            readOnly={readOnly}
            hasError={formRef?.current?.hasErrors(EXPERT_TIME_PATH)}
          />
        </div>
      }
      <div ref={signaturesRef}>
        <SectionHeading>
          <h3 css={formRef?.current?.hasErrors('caseInfo.signatures') && "color: #c3282d;"}><FormattedMessage
            defaultMessage="Signatures"
            description="Expert review signatures section title"
          />{isSignatureRequired ?<span css="color: #c3282d;">*</span> : null}</h3>
          <p css={formRef?.current?.hasErrors('caseInfo.signatures') && "color: #c3282d;"}><FormattedMessage
            defaultMessage="To sign your report, please add a signature"
            description="Expert review signatures section info"
          /></p>
        </SectionHeading>
        <Card css={"position:relative;" + (formRef?.current?.hasErrors('caseInfo.signatures') ? "border: 1px solid #c3282d;" : "")}>
          <React.Suspense fallback={<Loading absolute />}>
            <ExpertSignatureManagement path={['caseInfo','signatures']} disabled={readOnly} caseRequest={caseRequest} form={form}/>
          </React.Suspense>
        </Card>
      </div>
      <Errors/>
      <PageFooter>
        
        <div className="right-wrapper">
        {navigationButtons(form)}
        </div>
      </PageFooter>
    </PageContainer>
    <PurviewFooter/>
  </>
  }</WithForm>
}
